import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import TradingChart from '../../../components/TradingChart';
import { backtestRunApi } from '../../services/api';
import type {
  AIBacktestChartSymbolSummary,
  AIBacktestRunDetail,
  AIBacktestTrade,
} from '../../types';

interface BacktestChartReviewPanelProps {
  runDetail: AIBacktestRunDetail;
}

const TIMEFRAME_OPTIONS = [
  { value: 'M1', label: '1 Minute' },
  { value: 'M5', label: '5 Minutes' },
  { value: 'M15', label: '15 Minutes' },
  { value: 'M30', label: '30 Minutes' },
  { value: 'H1', label: '1 Hour' },
  { value: 'H4', label: '4 Hours' },
  { value: 'D1', label: '1 Day' },
  { value: 'W1', label: '1 Week' },
  { value: 'MN', label: '1 Month' },
];

const asNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrency = (value: unknown): string => {
  const numberValue = asNumber(value);
  if (numberValue === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(numberValue);
};

const formatNumber = (value: unknown, digits = 2): string => {
  const numberValue = asNumber(value);
  if (numberValue === null) return '-';
  return numberValue.toFixed(digits);
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const shortExecutionId = (id?: string | null): string => {
  if (!id) return '-';
  return id.length > 12 ? `${id.slice(0, 8)}...${id.slice(-4)}` : id;
};

const ExecutionLinkChip: React.FC<{ id?: string | null }> = ({ id }) => {
  if (!id) {
    return <Typography variant="caption" color="text.secondary">-</Typography>;
  }

  return (
    <Chip
      label={shortExecutionId(id)}
      size="small"
      variant="outlined"
      component={RouterLink}
      to={`/ai/executions/${id}`}
      clickable
    />
  );
};

const buildLocalSymbolSummaries = (
  trades: AIBacktestTrade[] | undefined,
): AIBacktestChartSymbolSummary[] => {
  const grouped = new Map<string, AIBacktestChartSymbolSummary>();

  for (const trade of trades || []) {
    const existing = grouped.get(trade.symbol) || {
      symbol: trade.symbol,
      total_trades: 0,
      winning_trades: 0,
      losing_trades: 0,
      open_trades: 0,
      net_pnl: 0,
      total_volume: 0,
      win_rate: 0,
    };

    const pnl = asNumber(trade.profit_loss);
    existing.total_trades += 1;
    existing.total_volume += asNumber(trade.volume) || 0;

    if (pnl === null) {
      existing.open_trades += 1;
    } else {
      existing.net_pnl += pnl;
      if (pnl >= 0) existing.winning_trades += 1;
      else existing.losing_trades += 1;
    }

    grouped.set(trade.symbol, existing);
  }

  return Array.from(grouped.values())
    .map((summary) => {
      const closed = summary.winning_trades + summary.losing_trades;
      return {
        ...summary,
        net_pnl: Number(summary.net_pnl.toFixed(2)),
        total_volume: Number(summary.total_volume.toFixed(8)),
        win_rate: closed ? Number(((summary.winning_trades / closed) * 100).toFixed(2)) : 0,
      };
    })
    .sort((a, b) => b.total_trades - a.total_trades || a.symbol.localeCompare(b.symbol));
};

export const BacktestChartReviewPanel: React.FC<BacktestChartReviewPanelProps> = ({
  runDetail,
}) => {
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [selectedTimeframe, setSelectedTimeframe] = useState('H1');
  const [selectedTradeId, setSelectedTradeId] = useState<string | null>(null);

  const localSymbols = useMemo(
    () => buildLocalSymbolSummaries(runDetail.trades),
    [runDetail.trades],
  );

  useEffect(() => {
    if (!localSymbols.length) {
      setSelectedSymbol('');
      return;
    }

    const stillAvailable = localSymbols.some(
      (summary) => summary.symbol === selectedSymbol,
    );
    if (!stillAvailable) {
      setSelectedSymbol(localSymbols[0].symbol);
    }
  }, [localSymbols, selectedSymbol]);

  const chartQuery = useQuery({
    queryKey: ['backtest-chart-review', runDetail.id, selectedSymbol, selectedTimeframe],
    queryFn: () =>
      backtestRunApi.getChartReview(runDetail.id, {
        symbol: selectedSymbol,
        timeframe: selectedTimeframe,
        max_bars: 8000,
      }),
    enabled: Boolean(runDetail.id && selectedSymbol),
    staleTime: 60_000,
  });

  const chartData = chartQuery.data;
  const symbolSummaries = chartData?.symbols?.length ? chartData.symbols : localSymbols;
  const stats = chartData?.stats || localSymbols.find(
    (summary) => summary.symbol === selectedSymbol,
  );
  const trades = chartData?.trades || runDetail.trades.filter(
    (trade) => trade.symbol === selectedSymbol,
  );
  const selectedTrade = trades.find((trade) => trade.id === selectedTradeId) || null;

  const tradeMarkers = useMemo(
    () =>
      trades.map((trade) => ({
        ...trade,
        pnl: asNumber(trade.profit_loss) || 0,
        entry_timestamp: trade.entry_timestamp
          ? new Date(trade.entry_timestamp).getTime()
          : undefined,
        exit_timestamp: trade.exit_timestamp
          ? new Date(trade.exit_timestamp).getTime()
          : undefined,
      })),
    [trades],
  );

  if (!runDetail.trades?.length) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No trades are available for chart review.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h6">Chart Review</Typography>
          <Typography variant="body2" color="text.secondary">
            Trade overlays by symbol on historical candles.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            select
            size="small"
            label="Symbol"
            value={selectedSymbol}
            onChange={(event) => {
              setSelectedSymbol(event.target.value);
              setSelectedTradeId(null);
            }}
            sx={{ minWidth: 180 }}
          >
            {symbolSummaries.map((summary) => (
              <MenuItem key={summary.symbol} value={summary.symbol}>
                {summary.symbol} ({summary.total_trades})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Timeframe"
            value={selectedTimeframe}
            onChange={(event) => {
              setSelectedTimeframe(event.target.value);
              setSelectedTradeId(null);
            }}
            sx={{ minWidth: 150 }}
          >
            {TIMEFRAME_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
        <Chip label={`${stats?.total_trades ?? 0} trades`} size="small" />
        <Chip label={`${formatNumber(stats?.win_rate, 1)}% win rate`} size="small" />
        <Chip
          label={`${formatCurrency(stats?.net_pnl)} net`}
          size="small"
          color={(stats?.net_pnl ?? 0) >= 0 ? 'success' : 'error'}
          variant="outlined"
        />
        <Chip label={`${stats?.open_trades ?? 0} open`} size="small" variant="outlined" />
        {chartData?.has_more_candles && (
          <Chip label={`First ${chartData.max_bars} candles shown`} size="small" color="warning" />
        )}
      </Stack>

      <Divider sx={{ my: 2 }} />

      {chartQuery.isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={420}>
          <CircularProgress />
        </Box>
      ) : chartQuery.isError ? (
        <Alert severity="error">
          Unable to load chart review data for {selectedSymbol}.
        </Alert>
      ) : chartData?.candles?.length ? (
        <Box sx={{ minHeight: 540 }}>
          <TradingChart
            ohlcvData={chartData.candles}
            tradeMarkers={tradeMarkers}
            isBacktest={true}
            selectedSymbol={selectedSymbol}
            selectedTimeframe={selectedTimeframe}
            height={520}
            selectedTrade={selectedTrade}
          />
        </Box>
      ) : (
        <Alert severity="warning">
          No candle data was found for {selectedSymbol} on {selectedTimeframe}. The
          trades are still listed below.
        </Alert>
      )}

      <Box mt={2}>
        <Typography variant="subtitle2" gutterBottom>
          Symbol Trades ({trades.length})
        </Typography>

        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Side</TableCell>
                <TableCell>Entry</TableCell>
                <TableCell>Exit</TableCell>
                <TableCell>SL / TP</TableCell>
                <TableCell align="right">P&L</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Open Exec</TableCell>
                <TableCell>Close Exec</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trades.map((trade) => {
                const pnl = asNumber(trade.profit_loss);
                const isSelected = trade.id === selectedTradeId;

                return (
                  <TableRow
                    key={trade.id}
                    hover
                    selected={isSelected}
                    onClick={() => setSelectedTradeId(trade.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{trade.trade_number}</TableCell>
                    <TableCell>
                      <Chip
                        label={trade.direction}
                        size="small"
                        color={trade.direction === 'BUY' ? 'success' : 'error'}
                        icon={
                          trade.direction === 'BUY' ? (
                            <TrendingUpIcon />
                          ) : (
                            <TrendingDownIcon />
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        {formatNumber(trade.entry_price, 5)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(trade.entry_timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        {formatNumber(trade.exit_price, 5)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(trade.exit_timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        SL {formatNumber(trade.stop_loss, 5)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        TP {formatNumber(trade.take_profit, 5)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          pnl === null
                            ? 'text.secondary'
                            : pnl >= 0
                              ? 'success.main'
                              : 'error.main',
                        fontWeight: 700,
                      }}
                    >
                      {formatCurrency(trade.profit_loss)}
                    </TableCell>
                    <TableCell>
                      <Chip label={trade.exit_reason} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <ExecutionLinkChip id={trade.ai_system_execution} />
                    </TableCell>
                    <TableCell>
                      <ExecutionLinkChip id={trade.closed_by_ai_system_execution} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
};

export default BacktestChartReviewPanel;
